import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { colorTokens } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { companyChannelService, CompanyChannel } from '@/services/companyChannelService';
import { useNavigationStore } from '@/common/store';
import { useAuthStore } from '@/store/useAuthStore';
import { useCompanyStore } from '@/store/useCompanyStore';
import NotFound from './NotFound';
import Avatar from '@/common/components/avatar/Avatar';
import { usePopupLogin } from '../hooks/usePopupLogin';
import { authService } from '@/services/authService';
import { toastUtils } from '@/common/utils/toastUtils';
import { companyService, RegisterUserAndCompanyData, RegisterCompanyData } from '@/services/companyService';
import { commonService } from '@/services/commonService';
import { aiService } from '@/services/aiService';
import { fileService } from '@/services/fileService';
import { extractDate } from '@/common/utils/dateFormat';
import { removeSpaces, validatePassword, validateLoginId } from '@/common/utils/commonUtil';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { LabelInput } from '@/common/components/input/LabelInput';
import { Button } from '@/common/components/button/Button';
import ViewSwitcher from '@/common/components/viewSwitcher/ViewSwitcher';
import FileUpload from '@/common/components/fileUpload/FileUpload';
import LoadingModal from '@/common/components/modal/LoadingModal';
import Config, { config } from '@/config';
import {
  ArrowRight,
  Briefcase,
  Building2,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  FileCheck,
  PartyPopper,
  Rocket,
  Shield,
  Sparkles,
  Star,
  User,
  Users,
  XCircle,
  Zap,
  AlertTriangle
} from 'lucide-react';

type RequestUserType = 'MEMBER_AND_HASCOMPANY' | 'MEMBER_AND_NOCOMPANY' | 'NO_MEMBER_AND_NOCOMPANY';

interface BusinessInfo {
  companyName: string;
  businessNumber: string;
  representative: string;
  corporateNumber: string;
  establishedDate: string;
}

enum ChannelStep {
  INITIAL,
  SIGNUP,
  COMPANY,
  WELCOME
}

const ChannelPage: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const {  } = useNavigationStore();
  const { user } = useAuthStore();
  const { companies } = useCompanyStore();

  const domain = config.domain;

  // 기본 상태
  const [channel, setChannel] = useState<CompanyChannel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<ChannelStep>(ChannelStep.INITIAL);
  const [requestUserType, setRequestUserType] = useState<RequestUserType | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 회원가입 관련 상태
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);
  const [certifyData, setCertifyData] = useState<InisisAuthResult | null>(null);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [isCompanyEmail, setIsCompanyEmail] = useState<boolean>(true);

  // 회사 정보 관련 상태
  const [isCertifiedCompany, setIsCertifiedCompany] = useState<boolean>(false);
  const [aiFilledFields, setAiFilledFields] = useState<Record<string, boolean>>({});
  
  // 사업자 정보 인증 관련 상태
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isBusinessVerified, setIsBusinessVerified] = useState<boolean | undefined>(undefined);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);

  // 카운트다운 상태
  const [countdown, setCountdown] = useState<number>(5);

  // 유효성 검사 에러 상태
  const [validationErrors, setValidationErrors] = useState<{
    loginId?: string | null;
    password?: string | null;
    confirmPassword?: string | null;
    companyCode?: string | null;
    secondEmail?: string | null;
    companyEmail?: string | null;
    businessNumber?: string | null;
    ceoName?: string | null;
  }>({});

  // 중복검사 상태
  const [isCheckingLoginId, setIsCheckingLoginId] = useState<boolean>(false);
  const [loginIdChecked, setLoginIdChecked] = useState<boolean>(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const [emailChecked, setEmailChecked] = useState<boolean>(false);
  const [isCheckingCompanyCode, setIsCheckingCompanyCode] = useState<boolean>(false);
  const [companyCodeChecked, setCompanyCodeChecked] = useState<boolean>(false);

  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyNameEn: '',
    ceoName: '',
    businessNumber: '',
    corporateNumber: '',
    establishedDate: '',
    ceoEmail: '',
    companyEmail: '',
    companyCode: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    loginId: '',
    password: '',
    passwordConfirm: '',
    userDi: '',
    userCi: '',
    token: ''
  });

  const aiFieldColor = colorTokens.brand[800];

  // 로그인 팝업
  const { openPopup } = usePopupLogin({
    onSuccess: async (data) => {
      setIsLoading(true);
      try {
        const responseUser = await authService.setUserProfile();
        
        if (!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }

        const updatedCompanies = useCompanyStore.getState().companies;
        
        if (updatedCompanies && updatedCompanies.length > 0) {
          // 회사가 있는 경우 바로 ColdCall로 이동
          setRequestUserType('MEMBER_AND_HASCOMPANY');
          setCurrentStep(ChannelStep.WELCOME);
        } else {
          // 회사가 없는 경우 회사 등록 단계로
          setRequestUserType('MEMBER_AND_NOCOMPANY');
          setCurrentStep(ChannelStep.COMPANY);
        }
      } catch (error) {
        console.error('로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    }
  });

  // Navigation 숨기기
  useEffect(() => {
    
    return () => {
      
    };
  }, []);

  // 채널 정보 로드
  useEffect(() => {
    const fetchChannel = async () => {
      if (!channelId) {
        setError('채널 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const channelData = await companyChannelService.getChannelByCode(channelId);
        
        if (!channelData) {
          setError('채널을 찾을 수 없습니다.');
        } else {
          setChannel(channelData);
        }
      } catch (err) {
        setError('채널 정보를 불러오는 중 오류가 발생했습니다.');
        console.error('Error fetching channel:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChannel();
  }, [channelId]);

  // 간편인증 처리
  const handleCertify = async (certifyData: InisisAuthResult) => {
    setIsLoading(true);
    try {
      if (certifyData.isDuplicate) {
        setIsCertifiedPersonal(false);
        setIsLoading(false);
        return toastUtils.error('이미 가입된 유저입니다.');
      }

      setIsCertifiedPersonal(true);
      setCertifyData(certifyData);
      setFormData(prev => ({
        ...prev,
        name: certifyData.userName,
        phone: certifyData.userPhone,
        userDi: certifyData.userDi,
        token: certifyData.token
      }));

      setRequestUserType('NO_MEMBER_AND_NOCOMPANY');
      setCurrentStep(ChannelStep.SIGNUP);
    } catch (error) {
      console.error('인증 처리 중 오류 발생:', error);
      toastUtils.error('인증 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 유효성 검사 함수들
  const validateEmail = (email: string): string | null => {
    if (!email) return '이메일을 입력해주세요.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return '올바른 이메일 형식을 입력해주세요.';
    return null;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword) return '비밀번호 확인을 입력해주세요.';
    if (password !== confirmPassword) return '비밀번호가 일치하지 않습니다.';
    return null;
  };

  const validateCompanyCode = (code: string): string | null => {
    if (!code) return '회사코드를 입력해주세요.';
    if (code.includes(' ')) return '회사코드에는 공백을 포함할 수 없습니다.';
    const codeRegex = /^[a-zA-Z0-9]+$/;
    if (!codeRegex.test(code)) return '회사코드는 영문과 숫자만 입력 가능합니다.';
    return null;
  };

  const handleValidation = (field: string, value: string) => {
    let error: string | null = null;
    
    switch (field) {
      case 'loginId':
        error = validateLoginId(value) as string | null;
        setLoginIdChecked(false);
        break;
      case 'personalEmail':
        error = value.length === 0 ? "개인 이메일을 입력해주세요." : validateEmail(value);
        setEmailChecked(false);
        setValidationErrors(prev => ({ ...prev, secondEmail: error }));
        return;
      case 'password':
        error = validatePassword(value) as string | null;
        if (passwordConfirm) {
          const confirmError = validateConfirmPassword(value, passwordConfirm);
          setValidationErrors(prev => ({ ...prev, confirmPassword: confirmError }));
        }
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(password, value);
        break;
      case 'companyCode':
        error = validateCompanyCode(value);
        setCompanyCodeChecked(false);
        break;
    }
    
    setValidationErrors(prev => ({ ...prev, [field]: error }));
  };

  // 중복 검사 함수들
  // 개인 이메일 중복 검사
  const handleCheckEmail = async () => {
    const email = formData.email;
    const validationError = validateEmail(email || '');
    if (validationError) {
      setValidationErrors(prev => ({ ...prev, secondEmail: validationError }));
      return;
    }
    
    setIsCheckingEmail(true);
    try {
      const result = await commonService.checkDuplicateValue('email', email || '');
      if (result.exists) {
        setValidationErrors(prev => ({ ...prev, secondEmail: '이미 사용 중인 이메일입니다.' }));
        setEmailChecked(false);
        toastUtils.error('이미 사용 중인 이메일입니다.');
      } else {
        setValidationErrors(prev => ({ ...prev, secondEmail: null }));
        setEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // 회사 이메일 중복 검사
  const [isCheckingCompanyEmail, setIsCheckingCompanyEmail] = useState<boolean>(false);
  const [companyEmailChecked, setCompanyEmailChecked] = useState<boolean>(false);

  const handleCheckCompanyEmail = async () => {
    const email = companyData.companyEmail;
    const validationError = validateEmail(email || '');
    if (validationError) {
      setValidationErrors(prev => ({ ...prev, companyEmail: validationError }));
      return;
    }
    
    setIsCheckingCompanyEmail(true);
    try {
      const result = await commonService.checkDuplicateValue('email', email || '');
      if (result.exists) {
        setValidationErrors(prev => ({ ...prev, companyEmail: '이미 사용 중인 이메일입니다.' }));
        setCompanyEmailChecked(false);
        toastUtils.error('이미 사용 중인 이메일입니다.');
      } else {
        setValidationErrors(prev => ({ ...prev, companyEmail: null }));
        setCompanyEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingCompanyEmail(false);
    }
  };

  const handleCheckLoginId = async () => {
    const loginId = formData.loginId;
    const validationError = validateLoginId(loginId || '');
    if (validationError) {
      setValidationErrors(prev => ({ ...prev, loginId: validationError as string }));
      return;
    }
    
    setIsCheckingLoginId(true);
    try {
      const result = await commonService.checkDuplicateValue('loginId', loginId || '');
      if (result.exists) {
        setValidationErrors(prev => ({ ...prev, loginId: '이미 사용 중인 아이디입니다.' }));
        setLoginIdChecked(false);
        toastUtils.error('이미 사용 중인 아이디입니다.');
      } else {
        setValidationErrors(prev => ({ ...prev, loginId: null }));
        setLoginIdChecked(true);
        toastUtils.success('사용 가능한 아이디입니다.');
      }
    } catch (error) {
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingLoginId(false);
    }
  };

  const handleCheckCompanyCode = async () => {
    const code = companyData.companyCode;
    const validationError = validateCompanyCode(code);
    if (validationError) {
      setValidationErrors(prev => ({ ...prev, companyCode: validationError }));
      return;
    }
    
    setIsCheckingCompanyCode(true);
    try {
      const result = await commonService.checkDuplicateValue('companyCode', code);
      if (result.exists) {
        setValidationErrors(prev => ({ ...prev, companyCode: '이미 사용 중인 회사코드입니다.' }));
        setCompanyCodeChecked(false);
        toastUtils.error('이미 사용 중인 회사코드입니다.');
      } else {
        setValidationErrors(prev => ({ ...prev, companyCode: null }));
        setCompanyCodeChecked(true);
        toastUtils.success('사용 가능한 회사코드입니다.');
      }
    } catch (error) {
      toastUtils.error('중복 검사 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingCompanyCode(false);
    }
  };

  // 포맷팅 함수들
  const formatBusinessNumber = (businessNumber: string) => {
    const cleanNumber = businessNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 10) {
      return `${cleanNumber.slice(0,3)}-${cleanNumber.slice(3,5)}-${cleanNumber.slice(5)}`;
    }
    return businessNumber;
  };

  const formatCorporateNumber = (corporateNumber: string) => {
    const cleanNumber = corporateNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 13) {
      return `${cleanNumber.slice(0, 6)}-${cleanNumber.slice(6)}`;
    }
    return corporateNumber;
  };

  const formatFoundingDate = (foundingDate: string) => {
    const cleanDate = foundingDate.replace(/[^0-9]/g, '');
    if (cleanDate.length === 8) {
      return `${cleanDate.slice(0,4)}-${cleanDate.slice(4,6)}-${cleanDate.slice(6)}`;
    }
    return foundingDate;
  };

  // 사업자 정보 자동 인증
  const autoCheckCompanyStatus = async (data: BusinessInfo) => {
    try {
      const representative = data.representative.split(",").map(name => name.trim());
      let isExistCompany = false;
      let matchedCeoName = '';
      
      for (let i = 0; i < representative.length; i++) {
        const statusResponse = await companyService.checkCompanyStatus({
          companyName: data.companyName,
          ceoName: representative[i],
          businessNumber: data.businessNumber.replace(/-/g, ''),
          corporateNumber: data.corporateNumber.replace(/-/g, ''),
          establishedDate: extractDate(data.establishedDate).replace(/-/g, '')
        });

        if (statusResponse.isApiError) {
          toastUtils.error('국세청 api 오류로 지연되고 있습니다. 1시간 후에 다시 시도해 주세요');
          setIsBusinessVerified(false);
          return;
        }

        if (statusResponse.isExistCompany) {
          isExistCompany = true;
          matchedCeoName = representative[i];
          break;
        }
      }

      if (isExistCompany) {
        setIsBusinessVerified(true);
        setIsCertifiedCompany(true);
        setCompanyData(prev => ({
          ...prev,
          companyName: data.companyName,
          ceoName: matchedCeoName,
          businessNumber: data.businessNumber,
          corporateNumber: data.corporateNumber,
          establishedDate: data.establishedDate,
        }));
        toastUtils.success('사업자 정보 인증이 완료되었습니다.');
      } else {
        toastUtils.error('사업자 정보 인증이 실패하였습니다. 법인등록번호 또는 설립일이 정확한지 확인해주세요.');
        setIsBusinessVerified(false);
      }
    } catch (error) {
      console.error('자동 회사 상태 확인 중 오류:', error);
      setIsBusinessVerified(false);
    }
  };

  // 사업자등록증 파일 업로드 후 AI 인증
  const handleVerification = async () => {
    if (isVerifying) return;

    if (!uploadedFile) {
      toastUtils.error('사업자등록증 파일을 먼저 업로드해주세요.');
      return;
    }
    
    setIsVerifying(true);
    try {
      const fileResult = await fileService.uploadFile([uploadedFile]);

      if (!fileResult) {
        toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');
        setIsVerifying(false);
        return;
      }
      
      const result = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id
      });

      if (result) {
        const data: BusinessInfo = {
          companyName: result.companyName,
          businessNumber: result.businessNumber,
          representative: result.representative,
          corporateNumber: result.corporateNumber,
          establishedDate: result.establishmentDate,
        };
        
        setBusinessInfo(data);

        if (data.companyName && data.businessNumber && data.representative && data.corporateNumber && data.establishedDate) {
          await autoCheckCompanyStatus(data);
        } else {
          setIsBusinessVerified(false);
        }
      } else {
        toastUtils.error('AI 분석에 실패했습니다. 파일을 확인해주세요.');
        setIsBusinessVerified(false);
      }
    } catch (error) {
      console.error('AI 분석 API 오류:', error);
      toastUtils.error('AI 분석 중 오류가 발생했습니다. 다시 시도해주세요.');
      setIsBusinessVerified(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // 회사 정보 입력 완료
  const handleCompanyComplete = async () => {
    if (!isBusinessVerified) {
      toastUtils.error('사업자 정보 인증을 먼저 완료해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      if (requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
        const bodyData: RegisterUserAndCompanyData = {
          notificationId: '',
          companyEmail: companyData.companyEmail,
          companyEmailVerified: true,
          companyCode: companyData.companyCode,
          isVerified: true,
          isPreStartup: false,
          isCeo: formData.name === companyData.ceoName,
          companyName: companyData.companyName,
          companyNameEn: companyData.companyNameEn || undefined,
          businessNumber: companyData.businessNumber,
          corporateNumber: companyData.corporateNumber,
          isBusinessVerified: true,
          foundingDate: formatFoundingDate(companyData.establishedDate),
          ceoName: companyData.ceoName,
          ceoEmail: companyData.ceoEmail,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          emailVerified: true,
          loginId: formData.loginId,
          password: formData.password,
          userDi: formData.userDi,
          userCi: formData.userCi,
          token: formData.token
        };
        const response = await companyService.registerUserAndCompany(bodyData);
        if (response.success) {
          setCurrentStep(ChannelStep.WELCOME);
        } else {
          toastUtils.error('회원 가입 중 오류가 발생했습니다.');
        }
      } else if (requestUserType === 'MEMBER_AND_NOCOMPANY') {
        const bodyData: RegisterCompanyData = {
          notificationId: '',
          companyEmail: companyData.companyEmail,
          companyEmailVerified: true,
          companyCode: companyData.companyCode,
          isVerified: true,
          isPreStartup: false,
          isCeo: user?.name === companyData.ceoName,
          companyName: companyData.companyName,
          companyNameEn: companyData.companyNameEn || undefined,
          businessNumber: companyData.businessNumber,
          corporateNumber: companyData.corporateNumber,
          isBusinessVerified: true,
          foundingDate: formatFoundingDate(companyData.establishedDate),
          ceoName: companyData.ceoName,
          ceoEmail: companyData.ceoEmail
        };
        const response = await companyService.registerCompany(bodyData);
        if (response.success) {
          setCurrentStep(ChannelStep.WELCOME);
        } else {
          toastUtils.error('회사 등록 중 오류가 발생했습니다.');
        }
      }
    } catch (error) {
      console.error('회사 정보 확인 중 오류 발생:', error);
      toastUtils.error('회사 정보 확인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // ColdCall로 이동
  const navigateToColdCall = useCallback(() => {
    const code = requestUserType === 'MEMBER_AND_HASCOMPANY' && companies && companies.length > 0
      ? companies[0].company.code
      : companyData.companyCode;
    window.location.replace(`${Config.startupClientUrl}/${code}/cold-call?channelId=${channelId}`);
  }, [requestUserType, companies, companyData.companyCode, channelId]);

  // 성공 단계에서 카운트다운
  useEffect(() => {
    if (currentStep === ChannelStep.WELCOME) {
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigateToColdCall();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [currentStep, navigateToColdCall]);

  // 로딩 화면
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner>
          <div className="spinner"></div>
          <LoadingText>채널 정보를 불러오는 중...</LoadingText>
        </LoadingSpinner>
      </LoadingContainer>
    );
  }

  if (error || !channel) {
    return <NotFound />;
  }

  /**
   * 1단계: 히어로 화면
   */
  const renderInitialStep = () => (
    <HeroSection>
      <HeroBackground>
        <GlowOrb $position="top-left" $delay={0} />
        <GlowOrb $position="top-right" $delay={2} />
        <GlowOrb $position="bottom" $delay={4} />
      </HeroBackground>
      
      <HeroWrapper>
        <HeroContent>
          <HeroTitle>
            <CompanyNameLarge>{channel.company.name}</CompanyNameLarge>
            <HeroSubtitle>
              함께 성장할 <GradientText>파트너</GradientText>를 찾고 있습니다
            </HeroSubtitle>
          </HeroTitle>
          
          <HeroDescription>
            지금 바로 지원하고 새로운 성장의 기회를 잡아보세요.
            <br />
            5-10분이면 지원서 작성이 완료됩니다.
          </HeroDescription>

          <HeroStats>
            <HeroStatItem>
              <Zap size={20} />
              <span>빠른 검토</span>
            </HeroStatItem>
            <HeroStatItem>
              <Star size={20} />
              <span>우선 검토</span>
            </HeroStatItem>
            <HeroStatItem>
              <Clock size={20} />
              <span>5-10분 소요</span>
            </HeroStatItem>
          </HeroStats>

          <HeroButtonGroup>
            <InisisAuthButton 
              $variant='fill'
              onSuccess={(data) => handleCertify(data)}
              $title='간편 인증으로 지원하기'
              $size='md'
            />
            <SecondaryButton onClick={openPopup} disabled={isLoading}>
              {isLoading ? '처리 중...' : '로그인으로 지원하기'}
            </SecondaryButton>
          </HeroButtonGroup>
        </HeroContent>

        <HeroVisual>
          <FloatingIconsContainer>
            <FloatingIcon $delay={0} $size="lg"><Rocket size={32} /></FloatingIcon>
            <FloatingIcon $delay={1} $size="md"><Sparkles size={24} /></FloatingIcon>
            <FloatingIcon $delay={2} $size="sm"><Star size={20} /></FloatingIcon>
            <FloatingIcon $delay={0.5} $size="md"><Briefcase size={26} /></FloatingIcon>
            <FloatingIcon $delay={1.5} $size="sm"><Users size={22} /></FloatingIcon>
          </FloatingIconsContainer>
        </HeroVisual>
      </HeroWrapper>
    </HeroSection>
  );

  /**
   * 2단계: 회원가입 정보 입력
   */
  const renderSignupStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        <StepIndicator>
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>2</StepCircle>
            <StepLabel $isActive={true}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>3</StepCircle>
            <StepLabel $isActive={false}>기업 설정</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>4</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <User size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>{formData.name}님, 잘 부탁드립니다! 🫡</CardTitle>
              <CardDescription>
                팩트시트 사용을 위해 계정 정보를 입력해 주세요.
              </CardDescription>
            </div>
          </CardHeader>

          <FormSection>
            <FormSectionTitle>
              <Check size={16} />
              <span>인증된 정보</span>
            </FormSectionTitle>
            <VerifiedInfoGrid>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>이름</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.name}</VerifiedInfoValue>
              </VerifiedInfoItem>
              <VerifiedInfoItem>
                <VerifiedInfoLabel>전화번호</VerifiedInfoLabel>
                <VerifiedInfoValue>{formData.phone}</VerifiedInfoValue>
              </VerifiedInfoItem>
            </VerifiedInfoGrid>
          </FormSection>

          <FormSection>
            <FormSectionTitle>
              <Shield size={16} />
              <span>계정 설정</span>
            </FormSectionTitle>
            
            <FormInputGroup>
              <FormInputRow>
                <LabelInput
                  label="아이디"
                  required
                  value={formData.loginId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, loginId: value }));
                    handleValidation('loginId', value);
                  }}
                  placeholder="아이디를 입력해주세요"
                  error={!!validationErrors.loginId}
                  errorMessage={validationErrors.loginId || ''}
                />
                <CheckButton
                  type="button"
                  $isChecked={loginIdChecked}
                  disabled={isCheckingLoginId || !formData.loginId || !!validationErrors.loginId}
                  onClick={handleCheckLoginId}
                >
                  {isCheckingLoginId ? '확인 중...' : loginIdChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>

              <FormInputRow>
                <LabelInput
                  label="개인 이메일"
                  required
                  value={formData.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, email: value }));
                    handleValidation('personalEmail', value);
                  }}
                  placeholder="개인 이메일을 입력해주세요"
                  error={!!validationErrors.secondEmail}
                  errorMessage={validationErrors.secondEmail || ''}
                />
                <CheckButton
                  type="button"
                  $isChecked={emailChecked}
                  disabled={isCheckingEmail || !formData.email || !!validationErrors.secondEmail}
                  onClick={handleCheckEmail}
                >
                  {isCheckingEmail ? '확인 중...' : emailChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>

              <FormGrid>
                <div>
                  <LabelInput
                    label="비밀번호"
                    required
                    type="password"
                    value={password}
                    onChange={(e) => {
                      const value = removeSpaces(e.target.value);
                      setPassword(value);
                      handleValidation('password', value);
                    }}
                    placeholder="비밀번호를 입력하세요"
                    error={!!validationErrors.password}
                  />
                  {validationErrors.password && (
                    <ErrorMessage>{validationErrors.password}</ErrorMessage>
                  )}
                </div>
                
                <div>
                  <LabelInput
                    label="비밀번호 확인"
                    required
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => {
                      const value = removeSpaces(e.target.value);
                      setPasswordConfirm(value);
                      handleValidation('confirmPassword', value);
                    }}
                    placeholder="비밀번호를 다시 입력하세요"
                    error={!!validationErrors.confirmPassword}
                  />
                  {validationErrors.confirmPassword && (
                    <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>
                  )}
                </div>
              </FormGrid>
            </FormInputGroup>
          </FormSection>
        </ContentCard>

        <NavigationButtonGroup>
          <BackButton onClick={() => setCurrentStep(ChannelStep.INITIAL)}>
            뒤로 가기
          </BackButton>
          <PrimaryButton
            onClick={() => {
              if (!loginIdChecked) {
                toastUtils.error('아이디 중복확인을 완료해주세요.');
                return;
              }
              if (!emailChecked) {
                toastUtils.error('개인 이메일 중복확인을 완료해주세요.');
                return;
              }
              if (validationErrors.password || validationErrors.confirmPassword) {
                toastUtils.error('비밀번호를 확인해주세요.');
                return;
              }
              if (!password || !passwordConfirm) {
                toastUtils.error('비밀번호를 입력해주세요.');
                return;
              }
              setFormData(prev => ({ ...prev, password: password, passwordConfirm: passwordConfirm }));
              setCurrentStep(ChannelStep.COMPANY);
            }}
            disabled={
              isLoading ||
              !!validationErrors.loginId ||
              !!validationErrors.password ||
              !!validationErrors.confirmPassword ||
              !formData.loginId ||
              !loginIdChecked ||
              !formData.email ||
              !emailChecked ||
              !password ||
              !passwordConfirm
            }
          >
            다음으로
            <ChevronRight size={18} />
          </PrimaryButton>
        </NavigationButtonGroup>
      </ContentWrapper>
    </CenteredContainer>
  );

  /**
   * 3단계: 회사 정보 설정
   */
  const renderCompanyStep = () => (
    <CenteredContainer>
      <ContentWrapper>
        <StepIndicator>
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>본인 확인</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true}>
            <StepCircle $isActive={true}><Check size={14} /></StepCircle>
            <StepLabel $isActive={true}>정보 입력</StepLabel>
          </StepItem>
          <StepLine $isActive={true} />
          <StepItem $isActive={true} $isCurrent={true}>
            <StepCircle $isActive={true} $isCurrent={true}>3</StepCircle>
            <StepLabel $isActive={true}>기업 설정</StepLabel>
          </StepItem>
          <StepLine $isActive={false} />
          <StepItem $isActive={false}>
            <StepCircle $isActive={false}>4</StepCircle>
            <StepLabel $isActive={false}>완료</StepLabel>
          </StepItem>
        </StepIndicator>

        <ContentCard>
          <CardHeader>
            <CardIconWrapper>
              <Building2 size={24} />
            </CardIconWrapper>
            <div>
              <CardTitle>기업 정보를 설정해주세요 🏢</CardTitle>
              <CardDescription>
                팩트시트에서 사용할 기업 정보를 입력해주세요.
              </CardDescription>
            </div>
          </CardHeader>

          {/* 사업자 정보 인증 섹션 */}
          <FormSection>
            <FormSectionTitle>
              <FileCheck size={16} />
              <span>사업자 정보 인증</span>
            </FormSectionTitle>

            {isBusinessVerified === undefined ? (
              <FormInputGroup>
                <Description>
                  최신 사업자등록증 파일을 업로드하고 인증 버튼을 눌러주세요.
                </Description>
                
                <FileUpload
                  type="md"
                  width="100%"
                  status={isVerifying ? 'uploading' : uploadedFile ? 'success' : 'idle'}
                  files={uploadedFile ? [uploadedFile] : []}
                  disabled={isVerifying}
                  accept=".pdf"
                  maxSize={10 * 1024 * 1024}
                  onChange={(files) => {
                    if (files.length > 0) {
                      setUploadedFile(files[0]);
                    } else {
                      setUploadedFile(null);
                      setIsBusinessVerified(undefined);
                    }
                  }}
                  label="여기에 파일을 끌어다 놓거나 클릭하세요"
                  onError={(error) => {
                    toastUtils.error(error);
                  }}
                />
                
          <Button
                  $size="md"
                  $variant="fill"
                  $isDisabled={!uploadedFile || isVerifying}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isVerifying) {
                      handleVerification();
                    }
                  }}
                  style={{ width: '100%', marginTop: '1rem' }}
          >
                  {isVerifying ? '인증 중...' : '인증하기'}
          </Button>
              </FormInputGroup>
            ) : isBusinessVerified ? (
              <FormInputGroup>
                <VerificationSuccessBox>
                  <CheckCircle size={20} color="#10b981" />
                  <span>사업자 정보 인증이 완료되었습니다.</span>
                </VerificationSuccessBox>
                
                {businessInfo && (
                  <BusinessInfoBox>
                    <BusinessInfoTitle>
                      <CheckCircle size={16} color="#10b981" />
                      <span>인증된 사업자 정보</span>
                    </BusinessInfoTitle>
                    <FormGrid>
                      <LabelInput label="사업자명" value={businessInfo.companyName} readOnly />
                      <LabelInput label="사업자등록번호" value={businessInfo.businessNumber} readOnly />
                      <LabelInput label="대표자" value={businessInfo.representative} readOnly />
                      <LabelInput label="법인등록번호" value={businessInfo.corporateNumber} readOnly />
                      <LabelInput label="설립일" value={businessInfo.establishedDate} readOnly />
                    </FormGrid>
                  </BusinessInfoBox>
                )}
              </FormInputGroup>
            ) : (
              <FormInputGroup>
                <WarningBox>
                  <AlertTriangle size={20} color="#f59e0b" />
                  <span>AI 분석이 완료되지 않았거나 일부 정보가 누락되었습니다. 정확한 정보를 직접 입력해주세요.</span>
                </WarningBox>
                
                <BusinessInfoBox>
                  <BusinessInfoTitle>
                    <AlertTriangle size={16} color="#f59e0b" />
                    <span>사업자 정보 직접 입력</span>
                  </BusinessInfoTitle>
                  <FormGrid>
                    <LabelInput
                      label="사업자명"
                      value={businessInfo?.companyName || ''}
                      onChange={(e) => setBusinessInfo(prev => prev ? { ...prev, companyName: e.target.value } : {
                        companyName: e.target.value, businessNumber: '', representative: '', corporateNumber: '', establishedDate: ''
                      })}
                      placeholder="사업자명을 입력하세요"
                    />
                    <LabelInput
                      label="사업자등록번호"
                      value={businessInfo?.businessNumber || ''}
                      maxLength={12}
                      onChange={(e) => {
                        const formattedValue = formatBusinessNumber(e.target.value);
                        setBusinessInfo(prev => prev ? { ...prev, businessNumber: formattedValue } : {
                          companyName: '', businessNumber: formattedValue, representative: '', corporateNumber: '', establishedDate: ''
                        });
                      }}
                      placeholder="000-00-00000"
                    />
                    <LabelInput
                      label="대표자"
                      value={businessInfo?.representative || ''}
                      onChange={(e) => {
                        setBusinessInfo(prev => prev ? { ...prev, representative: e.target.value } : {
                          companyName: '', businessNumber: '', representative: e.target.value, corporateNumber: '', establishedDate: ''
                        });
                      }}
                      placeholder="대표자명을 입력하세요"
                    />
                    <LabelInput
                      label="법인등록번호"
                      value={businessInfo?.corporateNumber || ''}
                      maxLength={14}
                      onChange={(e) => {
                        const formattedValue = formatCorporateNumber(e.target.value);
                        setBusinessInfo(prev => prev ? { ...prev, corporateNumber: formattedValue } : {
                          companyName: '', businessNumber: '', representative: '', corporateNumber: formattedValue, establishedDate: ''
                        });
                      }}
                      placeholder="123456-1234567"
                    />
                    <LabelInput
                      label="설립일"
                      value={businessInfo?.establishedDate || ''}
                      maxLength={10}
                      onChange={(e) => {
                        const formattedValue = formatFoundingDate(e.target.value);
                        setBusinessInfo(prev => prev ? { ...prev, establishedDate: formattedValue } : {
                          companyName: '', businessNumber: '', representative: '', corporateNumber: '', establishedDate: formattedValue
                        });
                      }}
                      placeholder="YYYY-MM-DD"
                    />
                  </FormGrid>
                  
                  <Button
                    $size="md"
                    $variant="fill"
                    $isDisabled={!businessInfo?.companyName || !businessInfo?.businessNumber ||
                      !businessInfo?.representative || !businessInfo?.corporateNumber ||
                      !businessInfo?.establishedDate}
                    onClick={() => {
                      if (businessInfo) {
                        autoCheckCompanyStatus(businessInfo);
                      }
                    }}
                    style={{ width: '100%', marginTop: '1rem' }}
                  >
                    정보 확인하기
                  </Button>
                </BusinessInfoBox>
              </FormInputGroup>
            )}
          </FormSection>

          {/* 회사 이메일 설정 (인증 완료 후에만 표시) */}
          {isBusinessVerified && (
            <FormSection>
              <FormSectionTitle>
                <Building2 size={16} />
                <span>회사 이메일 설정</span>
              </FormSectionTitle>
              <FormInputRow>
                <LabelInput
                  label='회사 이메일'
                  required
                  value={companyData.companyEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCompanyData(prev => ({ ...prev, companyEmail: value }));
                    setCompanyEmailChecked(false);
                    const error = validateEmail(value);
                    setValidationErrors(prev => ({ ...prev, companyEmail: error }));
                  }}
                  placeholder="회사 이메일을 입력해주세요"
                  error={!!validationErrors.companyEmail}
                  errorMessage={validationErrors.companyEmail || ''}
                />
                <CheckButton
                  type="button"
                  $isChecked={companyEmailChecked}
                  disabled={isCheckingCompanyEmail || !companyData.companyEmail || !!validationErrors.companyEmail}
                  onClick={handleCheckCompanyEmail}
                >
                  {isCheckingCompanyEmail ? '확인 중...' : companyEmailChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>
              <HelperText>
                투자사와의 커뮤니케이션에 사용될 회사 이메일을 입력해주세요.
              </HelperText>
            </FormSection>
          )}

          {/* 추가 기업 정보 (인증 완료 후에만 표시) */}
          {isBusinessVerified && (
            <FormSection>
              <FormSectionTitle>
                <Building2 size={16} />
                <span>추가 기업 정보</span>
              </FormSectionTitle>
              <FormInputRow>
                <LabelInput
                  label='회사 코드'
                  required
                  value={companyData.companyCode}
                  onChangeData={(value: string) => {
                    setCompanyData(prev => ({ ...prev, companyCode: value }));
                    handleValidation('companyCode', value);
                  }}
                  placeholder="영문/숫자로 입력해주세요"
                  error={!!validationErrors.companyCode}
                  errorMessage={validationErrors.companyCode || ''}
                />
                <CheckButton
                  type="button"
                  $isChecked={companyCodeChecked}
                  disabled={isCheckingCompanyCode || !companyData.companyCode || !!validationErrors.companyCode}
                  onClick={handleCheckCompanyCode}
                >
                  {isCheckingCompanyCode ? '확인 중...' : companyCodeChecked ? '확인 완료' : '중복확인'}
                </CheckButton>
              </FormInputRow>
              <HelperText>
                입력하신 값은 {domain}/{companyData.companyCode || 'example'} 형태로 접속 주소에 사용됩니다.
              </HelperText>
              
              <div style={{ marginTop: '1rem' }}>
                <LabelInput
                  label='영문 기업명'
                  value={companyData.companyNameEn}
                  onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, companyNameEn: value }))}
                  placeholder="영문 기업명을 입력해주세요 (선택)"
                />
              </div>
            </FormSection>
          )}
        </ContentCard>

        <NavigationButtonGroup>
          <BackButton onClick={() => {
            if (requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
              setCurrentStep(ChannelStep.SIGNUP);
            } else {
              setCurrentStep(ChannelStep.INITIAL);
            }
          }}>
            뒤로 가기
          </BackButton>
          <PrimaryButton
            onClick={handleCompanyComplete}
            disabled={
              !isBusinessVerified ||
              !companyData.companyEmail ||
              !companyEmailChecked ||
              !companyData.companyCode ||
              !companyCodeChecked ||
              isLoading
            }
          >
            {isLoading ? '처리 중...' : '가입 완료'}
            <ChevronRight size={18} />
          </PrimaryButton>
        </NavigationButtonGroup>
      </ContentWrapper>

      <LoadingModal
        isOpen={isLoading}
        message="처리 중입니다. 잠시만 기다려주세요."
      />
    </CenteredContainer>
  );

  /**
   * 4단계: 완료 및 ColdCall 이동
   */
  const renderWelcomeStep = () => (
    <SuccessContainer>
      <SuccessContent>
        <SuccessIconWrapper>
          <PartyPopper size={48} />
        </SuccessIconWrapper>
        
        <SuccessTitle>
          {requestUserType === 'MEMBER_AND_HASCOMPANY' ? '환영합니다!' : '가입이 완료되었습니다!'}
        </SuccessTitle>
        <SuccessDescription>
          {requestUserType === 'MEMBER_AND_HASCOMPANY'
            ? '이제 지원서 작성 페이지로 이동합니다.'
            : '축하합니다! 이제 지원서 작성 페이지로 이동합니다.'}
        </SuccessDescription>
        
        <SuccessCard>
          <SuccessCardHeader>
            <SuccessCardIcon>
              <Rocket size={24} />
            </SuccessCardIcon>
            <SuccessCardInfo>
              <SuccessCardTitle>지원서 작성 준비</SuccessCardTitle>
              <SuccessCardDesc>{channel.company.name} 채널에 지원합니다.</SuccessCardDesc>
            </SuccessCardInfo>
          </SuccessCardHeader>
          
          <SuccessProgressWrapper>
            <SuccessProgressBar>
              <SuccessProgressFill />
            </SuccessProgressBar>
          </SuccessProgressWrapper>
        </SuccessCard>

        <CountdownWrapper>
          <CountdownNumber>{countdown}</CountdownNumber>
          <CountdownText>초 후 자동으로 이동됩니다</CountdownText>
        </CountdownWrapper>

        <SuccessActionButton onClick={navigateToColdCall}>
          <span>바로 넘어가기</span>
          <ArrowRight size={18} />
        </SuccessActionButton>
      </SuccessContent>
    </SuccessContainer>
  );

  // 현재 단계에 따른 렌더링
  const renderCurrentStep = () => {
    switch (currentStep) {
      case ChannelStep.INITIAL:
        return renderInitialStep();
      case ChannelStep.SIGNUP:
        return renderSignupStep();
      case ChannelStep.COMPANY:
        return renderCompanyStep();
      case ChannelStep.WELCOME:
        return renderWelcomeStep();
      default:
        return renderInitialStep();
    }
  };

  return (
    <PageContainer>
      {renderCurrentStep()}
    </PageContainer>
  );
};

// ============================================
// Animations
// ============================================

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const successIconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 20px rgba(139, 92, 246, 0); }
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const countdownPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  height: 100vh;
  background: ${semanticColors.background.primary};
  overflow-x: hidden;
  overflow-y: auto;
`;

// Loading
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top-color: #60a5fa;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 4rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  overflow: hidden;
  
  @media (max-width: 968px) {
    padding: 4rem 2rem;
    min-height: auto;
  }
`;

const HeroWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 968px) {
    flex-direction: column;
    text-align: center;
    gap: 3rem;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const GlowOrb = styled.div<{ $position: string; $delay: number }>`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  animation: ${glowPulse} 6s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  ${props => props.$position === 'top-left' && `
    top: -100px;
    left: -100px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%);
  `}
  
  ${props => props.$position === 'top-right' && `
    top: 10%;
    right: -150px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%);
  `}
  
  ${props => props.$position === 'bottom' && `
    bottom: -100px;
    left: 30%;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
  `}
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 550px;
  z-index: 1;
  
  @media (max-width: 968px) {
    max-width: 100%;
  }
`;

const CompanyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  margin-bottom: 2rem;
  backdrop-filter: blur(8px);
  
  span {
    ${typography.paragraph.p2.medium}
    color: #fff;
  }
  
  @media (max-width: 968px) {
    margin: 0 auto 2rem;
  }
`;

const HeroTitle = styled.div`
  margin-bottom: 2rem;
`;

const CompanyNameLarge = styled.h1`
  font-size: 4.5rem;
  font-weight: 900;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 1rem;
  letter-spacing: -0.03em;
  text-shadow: 0 4px 30px rgba(255, 255, 255, 0.15);
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.25rem;
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 2rem;
    font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #34d399 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.8;
  margin-bottom: 2rem;
  
  strong {
    color: #fff;
  }
`;

const HeroStats = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  
  @media (max-width: 968px) {
    justify-content: center;
  flex-wrap: wrap;
  }
`;

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  ${typography.paragraph.p2.medium}
  
  svg {
    color: #60a5fa;
  }
`;

const HeroButtonGroup = styled.div`
  display: flex;
    flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 968px) {
    align-items: center;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.8);
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: #fff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeroVisual = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 400px;
  
  @media (max-width: 968px) {
    min-height: 280px;
    width: 100%;
  }
`;

const FloatingIconsContainer = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
`;

const FloatingIcon = styled.div<{ $delay: number; $size: 'sm' | 'md' | 'lg' }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  backdrop-filter: blur(8px);
  color: #fff;
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  ${props => props.$size === 'lg' && `
    width: 80px;
    height: 80px;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
  `}
  
  ${props => props.$size === 'md' && `
    width: 60px;
    height: 60px;
  `}
  
  ${props => props.$size === 'sm' && `
    width: 48px;
    height: 48px;
  `}
  
  &:nth-child(2) {
    top: 10%;
    left: 10%;
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.3);
  }
  
  &:nth-child(3) {
    top: 15%;
    right: 15%;
    background: rgba(251, 191, 36, 0.2);
    border-color: rgba(251, 191, 36, 0.3);
  }
  
  &:nth-child(4) {
    bottom: 30%;
    left: 5%;
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.3);
  }
  
  &:nth-child(5) {
    bottom: 20%;
    right: 10%;
    background: rgba(244, 63, 94, 0.2);
    border-color: rgba(244, 63, 94, 0.3);
  }
`;

// Centered Container (폼 화면용)
const CenteredContainer = styled.div`
  width: 100%;
  min-height: 100%;
  padding: 2rem;
  padding-bottom: 4rem;
  background: ${semanticColors.background.primary};
`;

const ContentWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// 스텝 인디케이터
const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const StepItem = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepCircle = styled.div<{ $isActive?: boolean; $isCurrent?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typography.paragraph.p2.medium}
  transition: all 0.3s ease;
  
  background: ${props =>
    props.$isActive
      ? semanticColors.background.brand
      : semanticColors.background.secondary};
  color: ${props =>
    props.$isActive
      ? '#fff'
      : semanticColors.text.tertiary};
  border: 2px solid ${props =>
    props.$isActive
      ? semanticColors.background.brand
      : semanticColors.border.primary};
  
  ${props => props.$isCurrent && `
    box-shadow: 0 0 0 4px ${semanticColors.background.brand}20;
  `}
`;

const StepLabel = styled.span<{ $isActive?: boolean }>`
  ${typography.caption.caption1.default}
  color: ${props => props.$isActive ? semanticColors.text.primary : semanticColors.text.tertiary};
  white-space: nowrap;
  
  @media (max-width: 640px) {
    display: none;
  }
`;

const StepLine = styled.div<{ $isActive?: boolean }>`
  width: 40px;
  height: 2px;
  margin: 0 0.75rem;
  background: ${props =>
    props.$isActive
      ? semanticColors.background.brand
      : semanticColors.border.primary};
  transition: background 0.3s ease;
  
  @media (max-width: 640px) {
    width: 24px;
  }
`;

// 콘텐츠 카드
const ContentCard = styled.div`
  background: ${semanticColors.background.secondary};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
`;

const CardIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.brand}10;
  border-radius: 12px;
  color: ${semanticColors.background.brand};
  flex-shrink: 0;
`;

const CardTitle = styled.h2`
  ${typography.heading.h5}
  color: ${semanticColors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const CardDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

// 폼 섹션
const FormSection = styled.div`
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const FormSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 1rem;
  
  svg {
    color: ${semanticColors.background.brand};
  }
`;

const VerifiedInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const VerifiedInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const VerifiedInfoLabel = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const VerifiedInfoValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const FormInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormInputRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  
  > div:first-child {
    flex: 1;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RadioGroup = styled.div`
  margin-bottom: 0.5rem;
`;

const RadioLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const CheckButton = styled.button<{ $isChecked?: boolean }>`
  padding: 0.625rem 1rem;
  margin-top: 1.75rem;
  border: 1px solid ${props =>
    props.$isChecked ? semanticColors.background.success.primary : semanticColors.background.brand};
  border-radius: 8px;
  background: ${props =>
    props.$isChecked ? semanticColors.background.success.primary : semanticColors.background.brand};
  color: #fff;
  ${typography.paragraph.p2.medium}
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 90px;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelperText = styled.div`
  font-size: 0.875rem;
  color: ${semanticColors.text.tertiary};
  margin-top: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const Description = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin-bottom: 1rem;
  line-height: 1.6;
`;

// 네비게이션 버튼 그룹
const NavigationButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
  
  @media (max-width: 640px) {
    flex-direction: column-reverse;
  }
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  background: transparent;
  color: ${semanticColors.text.secondary};
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${semanticColors.background.brand};
  border: none;
  border-radius: 10px;
  color: #fff;
  ${typography.paragraph.p1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 사업자 정보 인증 관련 스타일
const VerificationSuccessBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #d1fae5;
  border: 1px solid #34d399;
  border-radius: 8px;
  
  span {
    color: #065f46;
    font-weight: 500;
    ${typography.paragraph.p2.medium}
  }
`;

const BusinessInfoBox = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const BusinessInfoTitle = styled.h3`
  color: ${semanticColors.text.primary};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  
  span {
    color: #92400e;
    font-weight: 500;
    ${typography.paragraph.p2.default}
  }
`;

// 성공 화면 스타일
const SuccessContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow: auto;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(139, 92, 246, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SuccessContent = styled.div`
  max-width: 500px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const SuccessIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  border-radius: 50%;
  color: #fff;
  animation: ${successIconPulse} 2s ease-in-out infinite;
`;

const SuccessTitle = styled.h1`
  ${typography.heading.h3}
  color: #fff;
  margin-bottom: 0.75rem;
`;

const SuccessDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2rem;
`;

const SuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(8px);
`;

const SuccessCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SuccessCardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  color: #c4b5fd;
`;

const SuccessCardInfo = styled.div`
  text-align: left;
`;

const SuccessCardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: #fff;
  margin: 0;
`;

const SuccessCardDesc = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const SuccessProgressWrapper = styled.div`
  padding-top: 0.5rem;
`;

const SuccessProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const SuccessProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6 0%, #c4b5fd 100%);
  border-radius: 3px;
  animation: ${progressAnimation} 5s ease-in-out;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const CountdownNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #c4b5fd;
  animation: ${countdownPulse} 1s ease-in-out infinite;
`;

const CountdownText = styled.div`
  ${typography.paragraph.p2.default}
  color: rgba(255, 255, 255, 0.6);
`;

const SuccessActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #fff;
  border: none;
  border-radius: 12px;
  color: #1e1b4b;
  ${typography.paragraph.p1.medium}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.2);
  }
`;

export default ChannelPage; 
